/**
 * `<CustomTrackerBoard>` — the board surface for a free-form ("text")
 * tracker. Renders through the EXACT SAME components as the briefs board
 * (`TrackersBoardTable` → `TrackersProjectGroup` → row), so the two look
 * identical: colored group headers with the accent left stripe, the shared
 * column headers (rename / resize / reorder / configure / delete), the
 * sticky Item column, and the synced bottom scrollbar. The only differences
 * are the data source (tracker items, not briefs) and per-row behavior
 * (editable title, no comments, cell writes to the item).
 *
 * Board layout (columns + widths + custom groups) persists on the tracker
 * row via `PATCH /trackers/:id`, shared by every workspace member.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { arrayMove } from '@dnd-kit/sortable';
import { v4 as uuidv4 } from 'uuid';
import {
  trackerItemsQueryOptions,
  useCreateTrackerItemMutation,
  useDeleteTrackerItemMutation,
  useUpdateTrackerItemMutation,
  useUpdateTrackerMutation,
  type Tracker,
  type TrackerDashboard,
  type TrackerItem,
  type TrackerLayout,
  type TrackerViewType,
} from '@/entities/tracker';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { workspaceUsersQuery } from '@/entities/project-member';
import { notificationService } from '@/lib/services/notification.service';
import { track } from '@/shared/analytics';

import {
  COLUMN_TYPE_LABEL_KEY,
  CUSTOM_COLUMN_DEFAULT_WIDTH,
  CUSTOM_COLUMN_MIN_WIDTH_BY_TYPE,
  defaultStatusOptions,
  storedColumnType,
  type CustomColumn,
  type CustomColumnType,
  type ScrollContainersRef,
  type StatusOption,
  type SyncedScrollLeftRef,
} from '../../lib/useTrackersBoard';
import { buildTrackerSortChoices } from '../../lib/sort-choices';
import { useActiveView } from '../../lib/useActiveView';
import { useHasEnabledAutomations } from '../../lib/useHasEnabledAutomations';
import { BOARD_ROW_CAP } from '../../lib/board-scale.constants';
import { BoardCapNotice } from '../BoardCapNotice';
import { useCommentDeeplink } from '../../lib/useCommentDeeplink';
import {
  usePersistentFilterRules,
  usePersistentFilters,
  useRecentFilterColumns,
} from '../../lib/usePersistentFilters';
import {
  buildFilterColumns,
  compileFilters,
  compileRules,
  countFilterValues,
  filterMatchColumns,
  newlyNarrowingColumnIds,
  passesFilters,
  passesSearch,
  readCustomColumnValue,
} from '../../lib/filtering';
import { useFilterOptionLabels } from '../../lib/useFilterOptionLabels';
import { configToFields, fieldsToConfig } from '../../lib/column-config';
import { isReaderLabel, toReaderColumns, withStoredLabels } from '../../lib/shipped-labels';
import { renameFormulaReferences } from '../../lib/evaluateFormula';
import { useCanEditTracker } from '../../lib/tracker-access';
import { rowWriteRefusalNotice } from '../../lib/row-write-refusal';
import {
  buildGroups,
  countGroups,
  checkboxColumnSpec,
  customGroupSpec,
  peopleColumnSpec,
  statusColumnSpec,
  CUSTOM_GROUP_BY,
  GROUP_VALUE_KEY,
  NO_GROUP_COLOR,
  NO_GROUP_ID,
  type GroupBucket,
  type GroupingSpec,
} from '../../lib/grouping';
import {
  compareByColumn,
  compareBySortId,
  cycleColumnSort,
  type ColumnSort,
} from '../../lib/sorting';
import { BoardViewTabs } from '../BoardViewTabs';
import { StatusConfigModal } from '../StatusConfigModal';
import { ButtonConfigModal } from '../ButtonConfigModal';
import { FormulaConfigModal } from '../FormulaConfigModal';
import { TrackerCommentsPanel } from '../TrackerCommentsPanel';
import { TrackersToolbar } from '../TrackersToolbar';
import { DashboardView } from '../DashboardView';
import { KanbanView } from '../KanbanView';
import { TimelineView } from '../TimelineView';
import { TrackersBoardTable, type BoardGroup } from '../TrackersBoardTable';
import { useTranslation } from 'react-i18next';
import type { TFunction } from 'i18next';

/**
 * Brief-bound column types that make no sense on a free-form tracker. Excluding
 * `assets` is also why `columnsFromLayout` below can skip the assets relabelling
 * the briefs board applies on read: no layout here holds such a column.
 */
const EXCLUDED_TYPES: readonly CustomColumnType[] = ['brief_type', 'assets', 'project'];

/** Starter columns for a tracker whose layout was never saved. */
const starterColumns = (t: TFunction<'trackers'>): CustomColumn[] => [
  {
    id: 'starter_status',
    label: t('trackersBoard.customTrackerBoard.status'),
    type: 'status',
    statusOptions: defaultStatusOptions(),
  },
  { id: 'starter_due_date', label: t('trackersBoard.customTrackerBoard.dueDate'), type: 'date' },
  { id: 'starter_owner', label: t('trackersBoard.customTrackerBoard.owner'), type: 'people' },
  { id: 'starter_notes', label: t('trackersBoard.customTrackerBoard.notes'), type: 'text' },
];

const ITEM_COLUMN_WIDTH = 280;

/** Categorical column types you can group a board by. */
const GROUPABLE_TYPES = new Set<CustomColumnType>(['status', 'checkbox', 'people']);

/** A brand-new tracker opens with one group + one placeholder item. */
const STARTER_GROUP_ID = 'group_untitled';
const starterGroup = (t: TFunction<'trackers'>): StatusOption => ({
  id: STARTER_GROUP_ID,
  label: t('trackersBoard.customTrackerBoard.untitledGroup'),
  color: '#3b82f6',
});

/** Colors cycled through as the user adds custom groups. */
const NEW_GROUP_COLORS = [
  '#3b82f6',
  '#8b5cf6',
  '#ec4899',
  '#f97316',
  '#10b981',
  '#06b6d4',
  '#eab308',
  '#ef4444',
];

interface CustomTrackerBoardProps {
  tracker: Tracker;
}

/** Views a brand-new / never-configured tracker starts with. */
const DEFAULT_VIEWS: TrackerViewType[] = ['table'];

function defaultWidthFor(type: CustomColumnType): number {
  return Math.max(CUSTOM_COLUMN_DEFAULT_WIDTH, CUSTOM_COLUMN_MIN_WIDTH_BY_TYPE[type]);
}

/** layout DTO ⇄ widget CustomColumn conversion (per-type settings ride in config). */
function columnsFromLayout(layout: TrackerLayout | undefined): CustomColumn[] | null {
  if (!layout?.customColumns) return null;
  return layout.customColumns.flatMap((c) => {
    // Layout jsonb can hold partially-written columns at runtime despite the
    // non-optional DTO types; keep the guard.
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-condition
    if (!c.id || !c.label || !c.type) return [];
    const type = c.type as CustomColumnType;
    return [{ id: c.id, label: c.label, type, ...configToFields(type, c.config) }];
  });
}

function toLayout(
  columns: CustomColumn[],
  customGroups: StatusOption[],
  columnWidths: Record<string, number>,
  itemColumnWidth: number,
  dashboard: TrackerDashboard | null,
  views: TrackerViewType[],
): TrackerLayout {
  return {
    columnOrder: columns.map((c) => c.id),
    columnWidths,
    itemColumnWidth,
    customColumns: columns.map((c) => ({
      id: c.id,
      label: c.label,
      type: storedColumnType(c.type),
      ...fieldsToConfig(c),
    })),
    customGroups,
    // Layout PATCHes are full-replace — ALWAYS carry the dashboard + views
    // through, or any column/group edit would silently wipe them.
    ...(dashboard ? { dashboard } : {}),
    views,
  };
}

export function CustomTrackerBoard({ tracker }: CustomTrackerBoardProps) {
  const { t } = useTranslation('trackers');

  const workspaceId = useCurrentWorkspaceId() ?? '';
  const trackerId = tracker.id;

  const itemsQuery = useQuery(trackerItemsQueryOptions(workspaceId, trackerId));
  const hasEnabledAutomations = useHasEnabledAutomations(trackerId);
  const updateTracker = useUpdateTrackerMutation({ workspaceId });
  const canEdit = useCanEditTracker();
  const createItem = useCreateTrackerItemMutation({ workspaceId, trackerId });
  const updateItem = useUpdateTrackerItemMutation({
    workspaceId,
    trackerId,
    refetchForAutomations: hasEnabledAutomations,
  });
  const deleteItem = useDeleteTrackerItemMutation({ workspaceId, trackerId });

  // ── Layout state (columns + widths + custom groups), seeded once from the
  // tracker row, owned locally, and persisted together on every edit.
  const [columns, setColumns] = useState<CustomColumn[]>([]);
  // What the board shows. A column this board seeded from a template reads in
  // the language of whoever is looking, while `columns` keeps the words the
  // layout holds: everything persisted, and every formula, works off that one.
  // See `shipped-labels.ts` for why the two must not be collapsed.
  const readerColumns = useMemo(() => toReaderColumns(columns, t), [columns, t]);
  const [customGroups, setCustomGroups] = useState<StatusOption[]>([]);
  const [columnWidths, setColumnWidths] = useState<Record<string, number>>({});
  const [itemColumnWidth, setItemColumnWidth] = useState<number>(ITEM_COLUMN_WIDTH);
  const [dashboard, setDashboard] = useState<TrackerDashboard | null>(null);
  // Enabled views (ordered; first = opens-by-default), seeded synchronously
  // from the tracker prop so the board opens on the right view with no flash.
  const [views, setViews] = useState<TrackerViewType[]>(
    () => tracker.layout?.views ?? DEFAULT_VIEWS,
  );
  const { activeView, setActiveView } = useActiveView(views);
  const seededRef = useRef(false);
  const firstItemRef = useRef(false);
  // Captured once at seed time: was this a never-configured tracker? Used to
  // decide first-item seeding WITHOUT re-reading tracker.groupBy, which flips
  // truthy the moment the seed PATCH resolves (a race against the items load).
  const brandNewRef = useRef(false);

  // Grouping mode — persisted on the tracker row (shared by the workspace).
  // Defaults to Custom (the free-form board is always grouped, never headerless).
  const [groupById, setGroupByState] = useState<string>(tracker.groupBy ?? CUSTOM_GROUP_BY);
  const setGroupBy = (next: string | null) => {
    if (!next) return;
    setGroupByState(next);
    if (canEdit) updateTracker.mutate({ trackerId, body: { groupBy: next } });
    track('tracker_grouped', {
      tracker_id: trackerId,
      board_type: 'free_form',
      group_by:
        next === 'project'
          ? 'project'
          : next === CUSTOM_GROUP_BY
            ? 'custom'
            : (columns.find((c) => c.id === next)?.type ?? 'none'),
    });
  };

  useEffect(() => {
    if (seededRef.current) return;
    const fromLayout = columnsFromLayout(tracker.layout);
    const cols = fromLayout ?? starterColumns(t);
    setColumns(cols);
    if (tracker.layout?.columnWidths) setColumnWidths(tracker.layout.columnWidths);
    if (tracker.layout?.itemColumnWidth != null) setItemColumnWidth(tracker.layout.itemColumnWidth);
    const seededDashboard = tracker.layout?.dashboard ?? null;
    setDashboard(seededDashboard);

    let groups = Array.isArray(tracker.layout?.customGroups)
      ? (tracker.layout.customGroups as StatusOption[])
      : [];

    // Brand-new tracker: seed a single "Untitled Group", set Custom grouping,
    // and persist once so the DB reflects the default.
    if (!tracker.groupBy) {
      brandNewRef.current = canEdit;
      if (groups.length === 0) groups = [starterGroup(t)];
      setCustomGroups(groups);
      setGroupByState(CUSTOM_GROUP_BY);
      if (canEdit) {
        updateTracker.mutate({
          trackerId,
          body: {
            groupBy: CUSTOM_GROUP_BY,
            layout: toLayout(cols, groups, {}, ITEM_COLUMN_WIDTH, seededDashboard, views),
          },
        });
      }
    } else {
      setCustomGroups(groups);
    }
    seededRef.current = true;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tracker.layout]);

  const persistLayout = (
    nextColumns: CustomColumn[],
    nextGroups: StatusOption[],
    nextWidths: Record<string, number> = columnWidths,
    nextItemWidth: number = itemColumnWidth,
    nextDashboard: TrackerDashboard | null = dashboard,
    nextViews: TrackerViewType[] = views,
  ) => {
    setColumns(nextColumns);
    setCustomGroups(nextGroups);
    setColumnWidths(nextWidths);
    setItemColumnWidth(nextItemWidth);
    setDashboard(nextDashboard);
    setViews(nextViews);
    if (!canEdit) return;
    updateTracker.mutate({
      trackerId,
      body: {
        layout: toLayout(
          nextColumns,
          nextGroups,
          nextWidths,
          nextItemWidth,
          nextDashboard,
          nextViews,
        ),
      },
    });
  };
  const persistColumns = (next: CustomColumn[]) => persistLayout(next, customGroups);
  const persistGroups = (next: StatusOption[]) => persistLayout(columns, next);
  const persistDashboard = (next: TrackerDashboard) =>
    persistLayout(columns, customGroups, columnWidths, itemColumnWidth, next);
  const persistViews = (next: TrackerViewType[]) =>
    persistLayout(columns, customGroups, columnWidths, itemColumnWidth, dashboard, next);

  // ── View management (tabs + manage menu). `table` is pinned (non-removable).
  const addView = (view: TrackerViewType) => {
    if (views.includes(view)) return;
    persistViews([...views, view]);
    setActiveView(view);
    if (view !== 'table') {
      track('tracker_view_added', {
        tracker_id: trackerId,
        board_type: 'free_form',
        view_type: view,
      });
    }
  };
  const removeView = (view: TrackerViewType) => {
    if (view === 'table') return;
    persistViews(views.filter((v) => v !== view));
    track('tracker_view_removed', {
      tracker_id: trackerId,
      board_type: 'free_form',
      view_type: view,
    });
  };

  const [searchInput, setSearchInput] = useState('');
  const [sortId, setSortIdState] = useState<string | null>(null);
  // Per-column header sort — takes precedence over the toolbar sort; the two
  // are kept mutually exclusive.
  const [columnSort, setColumnSort] = useState<ColumnSort | null>(null);
  const setSortId = (next: string | null) => {
    setSortIdState(next);
    if (next) {
      setColumnSort(null);
      track('tracker_sorted', {
        tracker_id: trackerId,
        board_type: 'free_form',
        source: 'toolbar',
        direction: next.endsWith('-desc') ? 'desc' : 'asc',
      });
    }
  };
  const toggleColumnSort = (columnId: string) => {
    const nextSort = cycleColumnSort(columnSort, columnId);
    setColumnSort(nextSort);
    setSortIdState(null);
    track('tracker_sorted', {
      tracker_id: trackerId,
      board_type: 'free_form',
      source: 'header',
      direction: nextSort && nextSort.columnId === columnId ? nextSort.dir : 'none',
    });
  };
  // Persisted per-user in localStorage (private to this browser), unlike the
  // shared tracker layout. See usePersistentFilters.
  const [filters, setFilters] = usePersistentFilters(trackerId);
  const [filterRules, setFilterRules] = usePersistentFilterRules(trackerId);
  const [recentFilterColumns, noteFilterColumnUsed] = useRecentFilterColumns(trackerId);

  // Columns you can group by (categorical). Anything else falls back to the
  // user-defined Custom groups.
  // Grouping is where a status option's label becomes a kanban column header,
  // so these read from the reader's copy. Only the labels differ; the ids the
  // board writes a grouped item against are the same either way.
  const groupableColumns = readerColumns.filter((c) => GROUPABLE_TYPES.has(c.type));
  const groupColumn = readerColumns.find((c) => c.id === groupById && GROUPABLE_TYPES.has(c.type));
  const isCustomGrouping = !groupColumn;
  const effectiveGroupBy = groupColumn ? groupColumn.id : CUSTOM_GROUP_BY;

  // Workspace roster — only needed (and only fetched) when grouping by a
  // People/Owner column. Empty (e.g. Platform 401 locally) → every row lands
  // in the "No owner" catch-all.
  const usersQuery = useQuery({
    ...workspaceUsersQuery(workspaceId),
    enabled: Boolean(workspaceId) && groupColumn?.type === 'people',
  });
  const peopleBuckets = useMemo<GroupBucket[]>(
    () => (usersQuery.data?.data ?? []).map((u) => ({ id: u.id, label: u.name })),
    [usersQuery.data],
  );

  const groupingSpec = useMemo<GroupingSpec<TrackerItem>>(() => {
    if (!groupColumn) return customGroupSpec(customGroups);
    switch (groupColumn.type) {
      case 'checkbox':
        return checkboxColumnSpec(groupColumn);
      case 'people':
        return peopleColumnSpec(groupColumn, peopleBuckets);
      default:
        return statusColumnSpec(groupColumn);
    }
  }, [groupColumn, customGroups, peopleBuckets]);

  const [configuringColumnId, setConfiguringColumnId] = useState<string | null>(null);
  // The editors open on what the header says, so this is the reader's copy.
  // `withStoredLabels` is what keeps saving one from persisting that wording
  // for the options they left alone.
  const configuringColumn = configuringColumnId
    ? readerColumns.find((c) => c.id === configuringColumnId)
    : undefined;
  const [groupsEditorOpen, setGroupsEditorOpen] = useState(false);

  // ── Comments (right-side panel, URL-deeplinked per row)
  const { openRowKey, focusCommentId, openComments, closeComments } = useCommentDeeplink();

  const columnDefs = useMemo<
    Record<string, { label: string; minWidth: number; type?: CustomColumnType }>
  >(() => {
    const defs: Record<string, { label: string; minWidth: number; type?: CustomColumnType }> = {};
    for (const c of readerColumns) {
      defs[c.id] = {
        label: c.label,
        minWidth: CUSTOM_COLUMN_MIN_WIDTH_BY_TYPE[c.type],
        type: c.type,
      };
    }
    return defs;
  }, [readerColumns]);

  const columnOrder = useMemo(() => columns.map((c) => c.id), [columns]);

  const makeColumn = (type: CustomColumnType): CustomColumn => {
    const typeName = t(COLUMN_TYPE_LABEL_KEY[type]);
    const existing = new Set(columns.map((c) => c.label));
    let label = typeName;
    for (let i = 2; existing.has(label); i += 1) label = `${typeName} ${i}`;
    return {
      id: uuidv4(),
      label,
      type,
      ...(type === 'status' ? { statusOptions: defaultStatusOptions() } : {}),
      // Seed the button caption from the label so a fresh button reads sensibly
      // before it's configured.
      ...(type === 'button' ? { buttonCaption: label } : {}),
    };
  };

  const addColumn = (type: CustomColumnType) => {
    persistColumns([...columns, makeColumn(type)]);
    track('tracker_column_added', {
      tracker_id: trackerId,
      board_type: 'free_form',
      column_type: type,
    });
  };
  const addColumnAfter = (afterId: string, type: CustomColumnType) => {
    const idx = columns.findIndex((c) => c.id === afterId);
    const next = [...columns];
    next.splice(idx < 0 ? next.length : idx + 1, 0, makeColumn(type));
    persistColumns(next);
    track('tracker_column_added', {
      tracker_id: trackerId,
      board_type: 'free_form',
      column_type: type,
    });
  };
  const duplicateColumn = (columnId: string) => {
    const src = columns.find((c) => c.id === columnId);
    if (!src) return;
    const idx = columns.findIndex((c) => c.id === columnId);
    // Named after the header the user is looking at: the copy is theirs from
    // the moment it exists, so it should not resurrect the stored wording.
    const sourceLabel = readerColumns.find((c) => c.id === columnId)?.label ?? src.label;
    const copy: CustomColumn = {
      ...src,
      id: uuidv4(),
      label: t('trackersBoard.customTrackerBoard.columnCopy', { label: sourceLabel }),
      statusOptions: src.statusOptions?.map((o) => ({ ...o })),
    };
    const next = [...columns];
    next.splice(idx + 1, 0, copy);
    persistColumns(next);
  };
  const deleteColumn = (columnId: string) => {
    const columnType = columns.find((c) => c.id === columnId)?.type;
    if (groupById === columnId) setGroupBy(CUSTOM_GROUP_BY);
    persistColumns(columns.filter((c) => c.id !== columnId));
    if (columnType) {
      track('tracker_column_deleted', {
        tracker_id: trackerId,
        board_type: 'free_form',
        column_type: columnType,
      });
    }
  };
  const renameColumn = (columnId: string, nextLabel: string) => {
    const previous = columns.find((c) => c.id === columnId);
    if (!previous || previous.label === nextLabel) return;
    // The rename field was seeded from the header, so a dismissed edit returns
    // the reader's wording rather than the stored one.
    if (isReaderLabel(previous, nextLabel, t)) return;
    persistColumns(
      renameFormulaReferences(columns, previous.label, nextLabel).map((c) =>
        c.id === columnId ? { ...c, label: nextLabel } : c,
      ),
    );
  };
  const reorderColumns = (activeId: string, overId: string) => {
    const from = columns.findIndex((c) => c.id === activeId);
    const to = columns.findIndex((c) => c.id === overId);
    if (from < 0 || to < 0) return;
    persistColumns(arrayMove(columns, from, to));
  };
  const setColumnWidth = (columnId: string, width: number) =>
    persistLayout(columns, customGroups, { ...columnWidths, [columnId]: width }, itemColumnWidth);
  const updateStatusOptions = (columnId: string, options: StatusOption[]) => {
    persistColumns(
      columns.map((c) =>
        c.id === columnId
          ? { ...c, statusOptions: withStoredLabels(c.statusOptions ?? [], options, t) }
          : c,
      ),
    );
    track('tracker_column_status_configured', {
      tracker_id: trackerId,
      board_type: 'free_form',
      option_count: options.length,
    });
  };
  const updateButtonCaption = (columnId: string, caption: string) =>
    persistColumns(columns.map((c) => (c.id === columnId ? { ...c, buttonCaption: caption } : c)));
  const updateFormulaExpression = (columnId: string, expression: string) =>
    persistColumns(
      columns.map((c) => (c.id === columnId ? { ...c, formulaExpression: expression } : c)),
    );

  const items = useMemo(() => itemsQuery.data ?? [], [itemsQuery.data]);

  // Seed the first placeholder item once, for a brand-new empty tracker. Runs
  // only after the items query settles; `brandNewRef` (not tracker.groupBy)
  // gates it so the seed PATCH resolving mid-load can't cancel the seeding.
  useEffect(() => {
    if (firstItemRef.current) return;
    if (!seededRef.current || itemsQuery.isLoading) return;
    firstItemRef.current = true;
    if (brandNewRef.current && items.length === 0) {
      createItem.mutate(
        { title: t('trackersBoard.customTrackerBoard.yourFirstItem') },
        {
          onSuccess: (created) =>
            updateItem.mutate({
              itemId: created.id,
              body: { customColumnValuesPatch: { [GROUP_VALUE_KEY]: STARTER_GROUP_ID } },
            }),
        },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [itemsQuery.isLoading, items.length]);

  const setCellValue = (itemId: string, columnId: string, value: unknown) => {
    if (!items.some((i) => i.id === itemId)) return;
    // Only the edited cell goes out: the cached map can predate a rule's write
    // to another cell, and sending it whole would erase that write. A null
    // clears the cell.
    // The rollback puts the old value back, which on its own reads as the
    // board having ignored the edit, so a refusal names the cell and why.
    const columnLabel = readerColumns.find((c) => c.id === columnId)?.label;
    updateItem.mutate(
      {
        itemId,
        body: { customColumnValuesPatch: { [columnId]: value ?? null } },
        reportsErrors: true,
      },
      {
        onError: (error) =>
          notificationService.showError(
            ...rowWriteRefusalNotice(t, error, columnLabel, {
              row_not_found: 'trackersBoard.useRecordBoard.refusedItemNotFound',
            }),
          ),
      },
    );
    const columnType = columns.find((c) => c.id === columnId)?.type;
    if (columnType) {
      track('tracker_cell_edited', {
        tracker_id: trackerId,
        board_type: 'free_form',
        column_type: columnType,
      });
    }
  };

  /** Creates the item, then seeds one column value so it lands where it was asked for. */
  const addItemWithValue = (title: string, columnId: string | null, value: string | null) => {
    createItem.mutate(
      { title },
      {
        onSuccess: (created) => {
          track('tracker_item_created', { tracker_id: trackerId, board_type: 'free_form' });
          if (!columnId || value === null) return;
          updateItem.mutate({
            itemId: created.id,
            body: { customColumnValuesPatch: { [columnId]: value } },
          });
        },
      },
    );
  };

  const addItem = (groupId: string, title: string) => {
    // Only groupings whose bucket id IS the stored value can seed a row into
    // their group (Status, Custom). View-only groupings (checkbox/people)
    // create an unassigned row that lands in its natural bucket.
    const seedable = groupId !== NO_GROUP_ID && groupingSpec.editable;
    addItemWithValue(title, seedable ? (groupingSpec.key ?? null) : null, groupId);
  };

  const addItemWithStatus = (statusId: string | null, title: string) => {
    const statusColumn = columns.find((c) => c.type === 'status');
    addItemWithValue(title, statusColumn?.id ?? null, statusId);
  };

  const filterOptionLabels = useFilterOptionLabels();

  // The filter menu prints column and option names, so it reads from the
  // reader's copy. What a selection is matched against is the option id, so
  // `matchColumns` below is unaffected either way.
  const filterColumns = useMemo(
    () => buildFilterColumns(readerColumns, items, readCustomColumnValue, filterOptionLabels),
    [readerColumns, items, filterOptionLabels],
  );

  /**
   * What selections are matched against. Wider than `filterColumns`, which
   * hides a people column until the directory can name it: a persisted people
   * selection has to keep narrowing the board while those names load.
   */
  const matchColumns = useMemo(() => filterMatchColumns(columns), [columns]);

  const compiled = useMemo(
    () => [...compileFilters(filters, matchColumns), ...compileRules(filterRules, matchColumns)],
    [filters, filterRules, matchColumns],
  );

  /**
   * One instant for the whole board: the chip counts and the row matcher both
   * bucket dates against it, and two `new Date()`s can straddle midnight and
   * disagree about what is overdue.
   */
  const now = useMemo(() => new Date(), []);

  // Notes is a text column here, not a fixed field, so "search the notes" means
  // searching every text column.
  const searchColumnIds = useMemo(
    () => columns.filter((c) => c.type === 'text').map((c) => c.id),
    [columns],
  );

  const visibleItems = useMemo(() => {
    const query = searchInput.trim().toLowerCase();
    let result = items.filter(
      (i) =>
        passesSearch(i, query, searchColumnIds, readCustomColumnValue) &&
        passesFilters(i, compiled, readCustomColumnValue, now),
    );
    // A column-header sort overrides the toolbar sort and manual position order.
    const sortColumn = columnSort ? columns.find((c) => c.id === columnSort.columnId) : undefined;
    if (sortColumn && columnSort) {
      result = [...result].sort(compareByColumn<TrackerItem>(sortColumn, columnSort.dir));
      return result;
    }
    const comparator = compareBySortId<TrackerItem>(sortId, columns);
    result.sort(comparator ?? ((a, b) => a.position - b.position));
    return result;
  }, [items, searchInput, searchColumnIds, sortId, columnSort, compiled, now, columns]);

  const filterValueCounts = useMemo(
    () => countFilterValues(filterColumns, items, readCustomColumnValue, now),
    [filterColumns, items, now],
  );

  // A custom tracker has no fixed fields to sort on beyond the item name, so
  // the toolbar's list is grown from the tracker's own columns.
  const sortChoices = useMemo(() => buildTrackerSortChoices(readerColumns), [readerColumns]);

  // ── Sections → board groups (always grouped, every section has a header).
  // A section that renders no rows reads differently depending on whether it
  // is empty or merely filtered out, and the filtered rows can't say which —
  // so the same grouping runs once more over the unnarrowed items.
  const unfilteredCounts = useMemo(() => countGroups(items, groupingSpec), [items, groupingSpec]);

  const tableGroups = useMemo<BoardGroup[]>(
    () =>
      buildGroups(visibleItems, groupingSpec, (i: TrackerItem) => ({
        id: i.id,
        title: i.title,
        customColumnValues: i.customColumnValues as Record<string, unknown> | undefined,
        customColumnMeta: i.customColumnMeta ?? null,
      })).map((group) => ({
        ...group,
        unfilteredItemCount: unfilteredCounts.get(group.id) ?? 0,
      })),
    [visibleItems, groupingSpec, unfilteredCounts],
  );

  const groupIds = useMemo(() => tableGroups.map((g) => g.id), [tableGroups]);

  // Every group starts expanded; toggling is session-local.
  const [collapsedGroups, setCollapsedGroups] = useState<Set<string>>(new Set());
  const expandedGroupIds = useMemo(
    () => new Set(groupIds.filter((id) => !collapsedGroups.has(id))),
    [groupIds, collapsedGroups],
  );
  const toggleGroup = (id: string) =>
    setCollapsedGroups((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });

  // Group drag-reorder (custom grouping only): reorder the customGroups list.
  const handleGroupDragEnd = (activeId: string, overId: string) => {
    if (!isCustomGrouping) return;
    const from = customGroups.findIndex((g) => g.id === activeId);
    const to = customGroups.findIndex((g) => g.id === overId);
    if (from < 0 || to < 0) return;
    persistGroups(arrayMove(customGroups, from, to));
  };

  // ── Custom-group management (Group by: Custom) ──
  const addGroup = () => {
    const existing = new Set(customGroups.map((g) => g.label));
    let label: string = t('trackersBoard.customTrackerBoard.newGroup');
    for (let i = 2; existing.has(label); i += 1)
      label = t('trackersBoard.customTrackerBoard.newGroupNumbered', { number: i });
    const color = NEW_GROUP_COLORS[customGroups.length % NEW_GROUP_COLORS.length];
    persistGroups([...customGroups, { id: uuidv4(), label, color }]);
    track('tracker_custom_group_created', { tracker_id: trackerId, board_type: 'free_form' });
  };
  const renameGroup = (groupId: string, label: string) =>
    persistGroups(customGroups.map((g) => (g.id === groupId ? { ...g, label } : g)));
  // Items whose group key still points at the removed group fall into the
  // "Ungrouped" bucket — no per-item cleanup needed.
  const deleteGroup = (groupId: string) =>
    persistGroups(customGroups.filter((g) => g.id !== groupId));

  // Item drag-reorder within a group: reindex that group's items by position.
  const reorderItems = (groupId: string, activeId: string, overId: string) => {
    const group = tableGroups.find((g) => g.id === groupId);
    if (!group) return;
    const ids = group.items.map((i) => i.id);
    const from = ids.indexOf(activeId);
    const to = ids.indexOf(overId);
    if (from < 0 || to < 0) return;
    const reordered = arrayMove(ids, from, to);
    reordered.forEach((id, idx) => {
      const item = items.find((i) => i.id === id);
      if (item && item.position !== idx) updateItem.mutate({ itemId: id, body: { position: idx } });
    });
  };

  // Cross-group move: set the dragged item's group key (or clear it for the
  // Ungrouped bucket) and reindex the target group so it lands before
  // `beforeItemId` (append when null). Positions are per-group (sort re-buckets
  // first), so a local 0..n reindex is enough.
  const moveItem = (itemId: string, toGroupId: string, beforeItemId: string | null) => {
    // Only editable groupings wire onMoveItem, so `key` is always set here.
    const groupKey = groupingSpec.key;
    if (!groupKey) return;
    if (!items.some((i) => i.id === itemId)) return;
    const groupPatch = { [groupKey]: toGroupId === NO_GROUP_ID ? null : toGroupId };

    const target = tableGroups.find((g) => g.id === toGroupId);
    const targetIds = (target ? target.items.map((i) => i.id) : []).filter((id) => id !== itemId);
    const insertAt = beforeItemId ? targetIds.indexOf(beforeItemId) : -1;
    const finalIds =
      insertAt >= 0
        ? [...targetIds.slice(0, insertAt), itemId, ...targetIds.slice(insertAt)]
        : [...targetIds, itemId];

    finalIds.forEach((id, idx) => {
      if (id === itemId) {
        updateItem.mutate({ itemId, body: { position: idx, customColumnValuesPatch: groupPatch } });
      } else {
        const it = items.find((i) => i.id === id);
        if (it && it.position !== idx) updateItem.mutate({ itemId: id, body: { position: idx } });
      }
    });
  };

  // ── Kanban / Timeline adapters. Lane = the row's first bucket under the
  // current grouping (multi-owner rows show in their first owner's lane).
  const bucketById = useMemo(
    () => new Map(groupingSpec.buckets.map((b) => [b.id, b])),
    [groupingSpec],
  );
  const boardItems = useMemo(
    () =>
      visibleItems.map((item) => {
        const values = item.customColumnValues as Record<string, unknown> | undefined;
        const bucket = bucketById.get(groupingSpec.bucketIdsFor(item)[0] ?? '');
        return {
          id: item.id,
          title: item.title,
          contextLabel: bucket?.label,
          contextColor: bucket?.color,
          laneId: bucket?.id ?? NO_GROUP_ID,
          laneTitle:
            bucket?.label ?? (groupingSpec.noneLabel || t('trackersBoard.customTrackerBoard.none')),
          laneColor: bucket?.color ?? NO_GROUP_COLOR,
          values,
          // Powers the Dashboard view's default "items over time" line.
          createdAt: item.createdAt,
        };
      }),
    [visibleItems, groupingSpec, bucketById, t],
  );
  // Read off the compiled list, so a selection the board is not actually
  // applying cannot make a lane claim it was filtered.
  const hasActiveFilters = searchInput.length > 0 || compiled.length > 0;

  const handleSetStatus = (itemId: string, statusId: string | null) => {
    const statusColumn = columns.find((c) => c.type === 'status');
    if (!statusColumn) return;
    setCellValue(itemId, statusColumn.id, statusId);
  };

  // Refs for the shared horizontal-scroll sync in TrackersBoardTable.
  const scrollContainersRef: ScrollContainersRef = useRef<Set<HTMLDivElement>>(new Set());
  const syncedScrollLeftRef: SyncedScrollLeftRef = useRef(0);
  // The vertical scroll container the board rows virtualize against.
  const boardScrollRef = useRef<HTMLDivElement>(null);
  const tableMinContentWidth =
    itemColumnWidth +
    columns.reduce((sum, c) => sum + (columnWidths[c.id] ?? defaultWidthFor(c.type)), 0);

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-card">
      <TrackersToolbar
        searchValue={searchInput}
        onSearchChange={setSearchInput}
        sortId={sortId}
        onSortChange={setSortId}
        sortOptions={sortChoices}
        groupByOptions={[
          { value: CUSTOM_GROUP_BY, label: t('trackersBoard.customTrackerBoard.custom') },
          ...groupableColumns.map((c) => ({ value: c.id, label: c.label })),
        ]}
        groupByValue={effectiveGroupBy}
        onGroupByChange={(next) => {
          setGroupBy(next);
          if (canEdit && next === CUSTOM_GROUP_BY && customGroups.length === 0) {
            setGroupsEditorOpen(true);
          }
        }}
        filterColumns={filterColumns}
        filters={filters}
        onFiltersChange={(next) => {
          for (const key of Object.keys(next)) {
            if (key in filters) continue;
            const columnType = columns.find((c) => c.id === key)?.type;
            if (columnType) {
              track('tracker_filtered', {
                tracker_id: trackerId,
                board_type: 'free_form',
                column_type: columnType,
              });
            }
          }
          setFilters(next);
        }}
        filterPanel={{
          trackerId,
          rules: filterRules,
          activeFilterCount: compiled.length,
          onRulesChange: (next) => {
            for (const columnId of newlyNarrowingColumnIds(filterRules, next)) {
              const columnType = columns.find((c) => c.id === columnId)?.type;
              if (columnType) {
                track('tracker_filtered', {
                  tracker_id: trackerId,
                  board_type: 'free_form',
                  column_type: columnType,
                });
              }
            }
            setFilterRules(next);
          },
          counts: filterValueCounts,
          visibleItems: visibleItems.length,
          totalItems: items.length,
          recentColumnIds: recentFilterColumns,
          onColumnUsed: noteFilterColumnUsed,
        }}
        trailing={
          <BoardViewTabs
            views={views}
            activeView={activeView}
            onSwitch={(view) => {
              setActiveView(view);
              track('tracker_view_switched', {
                tracker_id: trackerId,
                board_type: 'free_form',
                view_type: view,
              });
            }}
            onAddView={addView}
            onRemoveView={removeView}
            onReorder={persistViews}
          />
        }
      />

      <div
        ref={boardScrollRef}
        className={`flex-1 overflow-y-auto px-4 ${activeView === 'table' ? '' : 'py-4'}`}
      >
        <BoardCapNotice total={items.length} cap={BOARD_ROW_CAP} />
        {activeView === 'kanban' ? (
          <KanbanView
            items={boardItems}
            customColumns={readerColumns}
            onSetStatus={handleSetStatus}
            onAddItem={addItemWithStatus}
            filtered={hasActiveFilters}
          />
        ) : null}
        {activeView === 'timeline' ? (
          <TimelineView items={boardItems} customColumns={readerColumns} />
        ) : null}
        {activeView === 'dashboard' ? (
          <DashboardView
            trackerId={trackerId}
            boardType="free_form"
            items={boardItems}
            // Also stored labels: a widget's title is saved when it is created,
            // so seeding it from the reader's copy would persist one.
            customColumns={columns}
            dashboard={dashboard}
            onDashboardChange={persistDashboard}
          />
        ) : null}
        {activeView === 'table' ? (
          <TrackersBoardTable
            groups={tableGroups}
            groupIds={groupIds}
            isLoading={itemsQuery.isLoading}
            expandedGroupIds={expandedGroupIds}
            onToggleGroup={toggleGroup}
            onGroupDragStart={() => {}}
            onGroupDragEnd={handleGroupDragEnd}
            onReorderItems={reorderItems}
            onMoveItem={groupingSpec.editable ? moveItem : undefined}
            onAddGroup={isCustomGrouping ? addGroup : undefined}
            onRenameGroup={isCustomGrouping ? renameGroup : undefined}
            onDeleteGroup={isCustomGrouping ? deleteGroup : undefined}
            columnOrder={columnOrder}
            columnWidths={columnWidths}
            onReorderColumns={reorderColumns}
            onSetColumnWidth={setColumnWidth}
            itemColumnWidth={itemColumnWidth}
            onSetItemColumnWidth={(w) => persistLayout(columns, customGroups, columnWidths, w)}
            scrollContainersRef={scrollContainersRef}
            syncedScrollLeftRef={syncedScrollLeftRef}
            boardScrollRef={boardScrollRef}
            tableMinContentWidth={tableMinContentWidth}
            excludeColumnTypes={EXCLUDED_TYPES}
            onAddCustomColumn={({ type }) => addColumn(type)}
            onAddCustomColumnAfter={(afterId, { type }) => addColumnAfter(afterId, type)}
            onDuplicateColumn={duplicateColumn}
            onDeleteColumn={deleteColumn}
            onConfigureColumn={setConfiguringColumnId}
            columnSort={columnSort}
            onToggleColumnSort={toggleColumnSort}
            columnDefs={columnDefs}
            customColumns={readerColumns}
            onRenameColumn={renameColumn}
            editableTitle
            trackerId={trackerId}
            showComments
            onOpenComments={openComments}
            openCommentsRowKey={openRowKey}
            onRenameItem={(id, next) => updateItem.mutate({ itemId: id, body: { title: next } })}
            onSetCellValue={setCellValue}
            onDeleteItem={(id) => deleteItem.mutate({ itemId: id })}
            onAddItem={addItem}
          />
        ) : null}
      </div>

      <StatusConfigModal
        open={configuringColumn?.type === 'status'}
        onOpenChange={(next) => {
          if (!next) setConfiguringColumnId(null);
        }}
        options={configuringColumn?.statusOptions ?? []}
        onSave={(next) => {
          if (configuringColumnId) updateStatusOptions(configuringColumnId, next);
        }}
      />
      <ButtonConfigModal
        open={configuringColumn?.type === 'button'}
        onOpenChange={(next) => {
          if (!next) setConfiguringColumnId(null);
        }}
        caption={configuringColumn?.buttonCaption ?? configuringColumn?.label ?? ''}
        onSave={(next) => {
          if (configuringColumnId) updateButtonCaption(configuringColumnId, next);
        }}
      />
      <FormulaConfigModal
        open={configuringColumn?.type === 'formula'}
        onOpenChange={(next) => {
          if (!next) setConfiguringColumnId(null);
        }}
        expression={configuringColumn?.formulaExpression ?? ''}
        // Stored labels, not the reader's: picking a column here writes its name
        // into the expression, which the evaluator later resolves by that name.
        columns={columns.filter((c) => c.id !== configuringColumnId)}
        onSave={(next) => {
          if (configuringColumnId) updateFormulaExpression(configuringColumnId, next);
        }}
      />
      <StatusConfigModal
        open={groupsEditorOpen}
        onOpenChange={setGroupsEditorOpen}
        title={t('trackersBoard.customTrackerBoard.configureGroups')}
        options={customGroups.length > 0 ? customGroups : [starterGroup(t)]}
        onSave={persistGroups}
      />
      <TrackerCommentsPanel
        open={openRowKey !== null}
        trackerId={trackerId}
        boardType="free_form"
        rowKey={openRowKey}
        focusCommentId={focusCommentId}
        onClose={closeComments}
      />
    </div>
  );
}
